/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable max-len */
import React from 'react';
import { isNil } from 'lodash';
import SimpleButton from '@/components/button-simple';
import TextInput from '@/components/text-input';
import TextArea from '@/components/text-area';
import FileUploader from '@/components/file-uploader';
import RadioButtons from '@/components/radio-buttons';
import ColorPicker from '@/components/color-picker';
import AddressSearch from '@/components/address-search';
import ToggleSwitch from '@/components/toggle-switch';
import Slider from '@/components/slider';
import EditableList from '@/components/editable-list';
import ToggleGroup from '@/components/toggle-group';
import TextField from '@/components/text-field';
import DropDown from '@/components/dropdown';
import CheckboxGroup from '@/components/checkbox-group/checkbox-group';
import GroupRequiredValidator from '@/components/group-required-validator/group-required-validator';
import ResendActivationEmail from '@/components/resend-activation-email';
import DayPicker from '@/components/single-date-picker';
import RangePicker from '@/components/date-picker';
import Multiselect from '@/components/multiselect';
import InlineEditableText from '@/components/inline-editable-text';
import EditableTextInput from '@/components/editable-text-input';
import EditableDropdown from '@/components/editable-dropdown';
import CustomAttributeSelector from '@/components/custom-attribute-selector';
import customAttributeConfig from '@/components/custom-attribute-selector/custom-attribute-config.json';
import ReturnItem from '@/components/return-item/return-item';
import Locations from '@/components/locations/locations';
import QRRenderer from '@/components/qr-renderer/qr-renderer';
import InlineEditableButton from '@/components/inline-editable-button/inline-editable-button';
import DecorativeImage from '@/components/decorative-image/decorative-image';
import ToggleList from '@/components/toggle-list';
import validation from '../../utilities/validation';
import { generateAttributeValueId, getAttributeUpdateKey } from '../page-customisation/utilities';
import EditableInStoreReturnsBlock from '../return-methods/editable-instore-returns-block';
import EditableRefundMethodsBlock from '../refunds/editable-refund-methods-block';
import EditableGiftReturnsBlock from '../gift-returns/editable-gift-returns-block';

const selectorConfig = customAttributeConfig.config;

const generateControls = (
  translate,
  getInputComponentValue,
  onChange,
  getComponentProps = () => {},
  selected = null,
  setSelected = () => {},
  onBlur = () => {},
) => {
  const generate = (
    { id, type, params },
    generateControl,
    enabled = true,
  ) => {
    const onChangeFunc = onChange({
      updateType: params.updateType,
      updateKey: params.updateValue,
      valueId: params.valueId,
    });

    const defaultValue = getInputComponentValue(params.valueId);

    switch (type) {
      case 'text-input':
        return (
          <TextInput
            ariaLabel={params.title ? `${translate(params.title)} ${translate('constants.inputAriaLabel')}` : translate('constants.inputAriaLabel')}
            key={id}
            selected={selected}
            onFocus={setSelected}
            onBlur={onBlur}
            inputSelectorId={id || params.title}
            formFieldId={id}
            tabIndex={params.tabIndex}
            title={params.title ? translate(params.title) : null}
            smallTitle={params.smallTitle}
            titleHeight={params.titleHeight}
            errorHeight={params.errorHeight}
            placeholder={(params.placeholder && !getComponentProps(params.valueId)?.shouldHideValue) ? translate(params.placeholder) : ''}
            numberRegex={validation.validationOptions[params.numberRegex]?.rule ?? null}
            maxLength={params.maxLength}
            validationRules={{
              onChange: params.onChangeValidationRules,
              onBlur: params.onBlurValidationRules,
              onSubmit: params.onSubmitValidationRules,
              requiredError: translate(params.requiredError),
              onChangeErrors: params.onChangeValidationErrors,
              onBlurErrors: params.onBlurValidationErrors,
              submitErrors: params.onSubmitValidationErrors,
              minLength: params.minLength,
              maxLength: params.maxLength,
              tooShortError: translate(params.tooShortError),
              tooLongError: translate(params.tooLongError),
            }}
            required={params.required}
            disabled={!enabled || params.disabled}
            defaultValue={defaultValue ?? ''}
            inputWidth={params.inputWidth}
            inputMaxWidth={params.inputMaxWidth}
            inputHeight={params.inputHeight}
            horizontalLayout={params.horizontalLayout}
            showCount={params.showCount}
            tooltipContent={params.tooltipContent ? translate(params.tooltipContent, params.tooltipData) : null}
            tooltipMaxWidth={params.tooltipContent ? params.tooltipMaxWidth : null}
            onChange={onChangeFunc}
            suffixIcon={getComponentProps(params.valueId)?.suffixIcon}
            shouldHideValue={getComponentProps(params.valueId)?.shouldHideValue}
            prefix={params.prefix}
            errorLeftAligned={params.errorLeftAligned}
            hideValidationErrors={params.hideValidationErrors}
            telemetryEvent={params.telemetryEvent}
            telemetryValue={params.telemetryValue}
            telemetryCustomProperty={params.telemetryCustomProperty}
            permissionId={params.permissionId}
          />
        );
      case 'text-area':
        return (
          <TextArea
            ariaLabel={params.title ? `${translate(params.title)} ${translate('constants.inputAriaLabel')}` : translate('constants.inputAriaLabel')}
            key={id}
            selected={selected}
            onFocus={setSelected}
            onBlur={onBlur}
            inputSelectorId={id}
            title={params.title ? translate(params.title) : null}
            placeholder={params.placeholder ? translate(params.placeholder) : ''}
            maxLength={params.maxLength}
            validationRules={{
              onChange: params.onChangeValidationRules,
              onBlur: params.onBlurValidationRules,
              onSubmit: params.onSubmitValidationRules,
              requiredError: translate(params.requiredError),
              onChangeErrors: params.onChangeValidationErrors,
              onBlurErrors: params.onBlurValidationErrors,
              submitErrors: params.onSubmitValidationErrors,
              minLength: params.minLength,
              maxLength: params.maxLength,
              tooShortError: translate(params.tooShortError),
              tooLongError: translate(params.tooLongError),
            }}
            required={params.required}
            defaultValue={defaultValue ?? ''}
            inputWidth={params.inputWidth}
            horizontalLayout={params.horizontalLayout}
            inputMaxHeight={params.inputMaxHeight}
            showCount={params.showCount}
            disabled={params.disabled}
            onChange={onChangeFunc}
            hasCopyButton={params.hasCopyButton}
            textAreaMinWidth={params.textAreaMinWidth}
            telemetryEvent={params.telemetryEvent}
            telemetryValue={params.telemetryValue}
            telemetryCustomProperty={params.telemetryCustomProperty}
          />
        );
      case 'button':
        return (
          <SimpleButton
            inputSelectorId={params.key}
            onClick={onChangeFunc}
            inputHeight={params.inputHeight}
            inputWidth={params.inputWidth}
            fullWidth={params.fullWidth}
            fontSize={params.fontSize}
            hasMargin={params.hasMargin}
            isLozenge={params.isLozenge}
            disabled={params.disabled}
            readOnly={params.readOnly}
            centered={params.centered}
            useMerchantThemeColor={params.useMerchantThemeColor}
            backgroundColor={params.useMerchantAccentColor ? getComponentProps()?.accentColor || params.backgroundColor : params.backgroundColor}
            textColor={params.textColor}
          >
            {translate(params.text)}
          </SimpleButton>
        );
      case 'radio-buttons':
        return (
          <RadioButtons
            ariaLabel={params.title ? `${translate(params.title)} ${translate('constants.radioButtonsAriaLabel')}` : translate('constants.radioButtonsAriaLabel')}
            inputSelectorId={id || params.title}
            title={params.title ? translate(params.title) : null}
            smallTitle={params.smallTitle}
            options={params.options?.map((option) => translate(option))}
            subTitleOptions={params.subTitleOptions?.map((option) => ({ title: translate(option.title), subTitle: translate(option.subTitle) }))}
            values={params.values}
            required={params.required}
            subControls={params.subControls}
            generateControl={generateControl}
            onChange={(index) => onChangeFunc(params.values[index])}
            defaultSelected={defaultValue}
            defaultValue={params.validateDefault && !isNil(defaultValue) ? String(defaultValue) : null}
            isHorizontal={params.isHorizontal}
            showBorders={params.showBorders}
            showSubtitle={params.showSubtitle}
            dynamicSubcontrols={params.dynamicSubcontrols}
            columnCount={params.columnCount}
            validationRules={{
              requiredError: translate(params.requiredError),
            }}
            disabled={params.disabled}
            telemetryEvent={params.telemetryEvent}
            telemetryValue={params.telemetryValue}
            telemetryCustomProperty={params.telemetryCustomProperty}
            permissionId={params.permissionId}
          />
        );
      case 'checkbox-group':
        return (
          <CheckboxGroup
            ariaLabel={translate('constants.checkboxGroupAriaLabel')}
            inputSelectorId={id}
            options={params.options.map((option) => ({ title: translate(option.title), subTitle: translate(option.subTitle) }))}
            required={params.required}
            subControls={params.subControls}
            generateControl={generateControl}
            onChange={(value) => onChangeFunc(value)}
            changeKey={params.updateValue}
            defaultValue={defaultValue ?? false}
            compactSubControls={params.compactSubControls}
            subControlsTitle={params.subControlsTitle ? translate(params.subControlsTitle) : null}
            subControlsRequired={params.subControlsRequired}
          />
        );
      case 'color-picker':
        return (
          <ColorPicker
            inputSelectorId={params.key}
            color={defaultValue}
            onChange={onChangeFunc}
            enabled={enabled}
            telemetryEvent={params.telemetryEvent}
            permissionId={params.permissionId}
          />
        );
      case 'file-upload':
        return (
          <FileUploader
            inputSelectorId={id}
            onChange={onChangeFunc}
            buttonText={translate(params.text)}
            isClearable={params.isClearable}
            clearButtonText={translate(params.clearText)}
            currentFile={defaultValue}
            fileType={params.fileType}
            telemetryEvent={params.telemetryEvent}
            validationRules={{
              minWidth: params.minWidth,
              minHeight: params.minHeight,
              maxHeight: params.maxHeight,
              maxWidth: params.maxWidth,
              imageTooSmallError: params.imageTooSmallError,
              imageTooLargeError: params.imageTooLargeError,
            }}
            enabled={enabled}
            permissionId={params.permissionId}
          />
        );
      case 'address-search':
        return (
          <AddressSearch
            generateControl={generateControl}
            addressFields={params.addressFields}
          />
        );
      case 'toggle-switch':
        return (
          <ToggleSwitch
            ariaLabel={`${translate(params.text)} ${translate('constants.toggleSwitchAriaLabel')}`}
            inputSelectorId={id}
            onChange={onChangeFunc}
            label={translate(params.text)}
            subControls={params.subControls}
            generateControl={generateControl}
            defaultValue={defaultValue}
            controlled={params.controlled}
            marginBottom={params.marginBottom}
            mainControlHidden={params.mainControlHidden}
            addChangeOnRender={params.addChangeOnRender}
            telemetryToggleOnEvent={params.telemetryToggleOnEvent}
            telemetryOnChangeEvent={params.telemetryOnChangeEvent}
            telemetryValue={params.telemetryValue}
            telemetryCustomProperty={params.telemetryCustomProperty}
            permissionId={params.permissionId}
          />
        );
      case 'slider':
        return (
          <Slider
            ariaLabel={`${translate(params.title)} ${translate('constants.sliderAriaLabel')}`}
            inputSelectorId={id}
            title={params.title}
            onChange={onChangeFunc}
            minimumValue={params.min}
            maximumValue={params.max}
            step={params.step}
            defaultValue={defaultValue}
            telemetryEvent={params.telemetryEvent}
            permissionId={params.permissionId}
          />
        );
      case 'editable-list':
        return (
          <EditableList
            ariaLabel={translate('constants.editableListAriaLabel')}
            inputSelectorId={id}
            onChange={onChangeFunc}
            items={defaultValue}
            switchesEnabled={params.switchesEnabled}
            switchesControlled={params.switchesControlled}
            maxLength={params.maxLength}
            telemetryOnChangeEvent={params.telemetryOnChangeEvent}
            telemetryToggleOffEvent={params.telemetryToggleOffEvent}
          />
        );
      case 'toggle-switch-group':
        return (
          <ToggleGroup
            ariaLabel={translate('constants.toggleGroupAriaLabel')}
            inputSelectorId={id}
            onChange={onChangeFunc}
            items={defaultValue}
          />
        );
      case 'text-field':
        return (
          <TextField
            key={id}
            inputSelectorId={id}
            title={params.title ? translate(params.title) : null}
            value={defaultValue}
            boldTitle={params.boldTitle}
            fullWidth={params.fullWidth}
            largeFont={params.largeFont}
          />
        );
      case 'dropdown':
        return (
          <DropDown
            ariaLabel={params.title ? `${translate(params.title)} ${translate('constants.dropdownAriaLabel')}` : translate('constants.dropdownAriaLabel')}
            key={id}
            inputSelectorId={id}
            title={params.title ? translate(params.title) : null}
            smallTitle={params.smallTitle}
            isSearchable={params.isSearchable}
            defaultValue={defaultValue}
            onChange={onChangeFunc}
            inputWidth={params.inputWidth}
            items={params.items}
            required={params.required}
            placeholder={params.placeholder ? translate(params.placeholder) : ''}
            validationRules={{
              onChange: params.onChangeValidationRules,
              onSubmit: params.onSubmitValidationRules,
              requiredError: translate(params.requiredError),
            }}
          />
        );
      case 'resend-activation-email-button':
        return (
          <ResendActivationEmail
            selectorId={id}
            title={params.title ? translate(params.title) : null}
            merchantData={defaultValue}
          />
        );
      case 'date-picker':
        return (
          <DayPicker
            key={id}
            inputSelectorId={id}
            title={params.title ? translate(params.title) : null}
            smallTitle={params.smallTitle}
            placeholder={params.placeholder ? translate(params.placeholder) : ''}
            value={defaultValue}
            onChange={onChangeFunc}
            required={params.required}
            validationRules={{
              requiredError: translate(params.requiredError),
            }}
            defaultValue={defaultValue}
          />
        );
      case 'range-picker':
        return (
          <RangePicker
            key={id}
            inputSelectorId={id}
            onRangeChange={onChangeFunc}
            defaultStartDate={defaultValue}
            defaultEndDate={defaultValue}
            showError={params.showError}
          />
        );
      case 'multiselect':
        return (
          <Multiselect
            key={id}
            inputSelectorId={id}
            title={params.title ? translate(params.title) : null}
            placeholder={params.placeholder ? translate(params.placeholder) : ''}
            smallTitle={params.smallTitle}
            defaultValue={defaultValue}
            onChange={onChangeFunc}
            inputWidth={params.inputWidth}
            noDropDownListGap={params.noDropDownListGap}
            fileImportTooltip={params.fileImportTooltip}
            withFileImport={params.withFileImport}
            required={params.required}
            validationRules={{
              requiredError: translate(params.requiredError),
            }}
          />
        );
      case 'inline-editable-text': {
        const inlineEditableTextDisplayValueId = params.displayValueId || params.displayValueIds?.value || params.valueId;

        return (
          <InlineEditableText
            ariaLabel={params.title ? `${translate(params.title)} ${translate('constants.inputAriaLabel')}` : translate('constants.inputAriaLabel')}
            key={id}
            inputSelectorId={id || params.title}
            maxLength={params.maxLength}
            fontSize={params.fontSize}
            fontWeight={params.fontWeight}
            errorLeftAligned={params.errorLeftAligned}
            hideValidationErrors={params.hideValidationErrors}
            subtitle={params.subtitle}
            partiallyEditable={params.partiallyEditable}
            uneditableContent={translate(params.uneditableContent)}
            validationRules={{
              onSubmit: params.onSubmitValidationRules,
              onChange: params.onChangeValidationRules,
              onChangeErrors: params.onChangeValidationErrors,
              submitErrors: params.onSubmitValidationErrors,
              tooLongError: translate(params.tooLongError),
            }}
            defaultValue={getInputComponentValue(inlineEditableTextDisplayValueId) ?? ''}
            defaultResetValue={params.defaultResetValue}
            textAreaMode={params.textAreaMode}
            checkboxMode={params.checkboxMode}
            asLink={params.asLink}
            centered={params.centered}
            inputTextAlignment={params.inputTextAlignment}
            readOnly={params.readOnly || !getComponentProps()?.globalEditEnabled}
            onChange={onChangeFunc}
          />
        );
      }
      case 'editable-text-input': {
        const titleDisplayValueId = params.displayValueIds?.title || params.updateValues?.title?.valueId;
        const placeholderDisplayValueId = params.displayValueIds?.placeholder || params.updateValues?.placeholder?.valueId;
        const tooltipDisplayValueId = params.displayValueIds?.tooltip || params.updateValues?.tooltip?.valueId;

        return (
          <EditableTextInput
            key={id}
            inputSelectorId={id}
            titleRules={{
              onSubmit: params.titleRules?.onSubmitValidationRules,
              onChange: params.titleRules?.onChangeValidationRules,
              onChangeErrors: params.titleRules?.onChangeValidationErrors,
              submitErrors: params.titleRules?.onSubmitValidationErrors,
              maxLength: params.titleRules?.maxLength,
              tooLongError: translate(params.titleRules?.tooLongError),
            }}
            placeholderRules={{
              onSubmit: params.placeholderRules?.onSubmitValidationRules,
              onChange: params.placeholderRules?.onChangeValidationRules,
              onChangeErrors: params.placeholderRules?.onChangeValidationErrors,
              submitErrors: params.placeholderRules?.onSubmitValidationErrors,
              maxLength: params.placeholderRules?.maxLength,
              tooLongError: translate(params.placeholderRules?.tooLongError),
            }}
            titleProps={{
              readOnly: params.titleProps?.readOnly || !getComponentProps()?.globalEditEnabled,
              textAreaMode: params.titleProps?.textAreaMode,
              ariaLabel: params.titleProps?.ariaLabel || `${translate('constants.inputAriaLabel')}`,
            }}
            placeholderProps={{
              readOnly: params.placeholderProps?.readOnly || !getComponentProps()?.globalEditEnabled,
              textAreaMode: params.placeholderProps?.textAreaMode,
              minHeight: params.placeholderProps?.minHeight,
              calenderIcon: params.placeholderProps?.calenderIcon,
              maxWidth: params.placeholderProps?.maxWidth,
              ariaLabel: params.titleProps?.ariaLabel || `${translate('constants.inputAriaLabel')}`,
            }}
            calenderIcon={params.calenderIcon}
            tooltipConfig={params.tooltipParams}
            showAsRequired={params.showAsRequired}
            defaultTitle={getInputComponentValue(titleDisplayValueId) ?? ''}
            defaultPlaceholder={getInputComponentValue(placeholderDisplayValueId) ?? ''}
            defaultTooltip={getInputComponentValue(tooltipDisplayValueId) ?? ''}
            defaultResetValueTitle={params.defaultResetValueTitle}
            defaultResetValuePlaceholder={params.defaultResetValuePlaceholder}
            onChangeTitle={onChange({
              updateType: params.updateValues?.title?.updateType,
              updateKey: params.updateValues?.title?.updateValue,
              valueId: params.updateValues?.title?.valueId,
            })}
            onChangeInput={onChange({
              updateType: params.updateValues?.placeholder?.updateType,
              updateKey: params.updateValues?.placeholder?.updateValue,
              valueId: params.updateValues?.placeholder?.valueId,
            })}
            onChangeTooltip={onChange({
              updateType: params.updateValues?.tooltip?.updateType,
              updateKey: params.updateValues?.tooltip?.updateValue,
              valueId: params.updateValues?.tooltip?.valueId,
            })}
          />
        );
      }
      case 'editable-dropdown':
        return (
          <EditableDropdown
            key={id}
            inputSelectorId={id}
            titleRules={{
              onSubmit: params.titleRules?.onSubmitValidationRules,
              onChange: params.titleRules?.onChangeValidationRules,
              onChangeErrors: params.titleRules?.onChangeValidationErrors,
              submitErrors: params.titleRules?.onSubmitValidationErrors,
              maxLength: params.titleRules?.maxLength,
              tooLongError: translate(params.titleRules?.tooLongError),
            }}
            placeholderRules={{
              onSubmit: params.placeholderRules?.onSubmitValidationRules,
              onChange: params.placeholderRules?.onChangeValidationRules,
              onChangeErrors: params.placeholderRules?.onChangeValidationErrors,
              submitErrors: params.placeholderRules?.onSubmitValidationErrors,
              maxLength: params.placeholderRules?.maxLength,
              tooLongError: translate(params.placeholderRules?.tooLongError),
            }}
            title={getInputComponentValue(params.updateValues?.title?.valueId) ?? ''}
            placeholder={getInputComponentValue(params.updateValues?.placeholder?.valueId) ?? ''}
            showAsRequired={params.showAsRequired}
            inputWidth={params.inputWidth}
            inputHeight={params.inputHeight}
            defaultResetValueTitle={params.defaultResetValueTitle}
            defaultResetValuePlaceholder={params.defaultResetValuePlaceholder}
            titleProps={{
              readOnly: params.titleProps?.readOnly || !getComponentProps()?.globalEditEnabled,
              textAreaMode: params.titleProps?.textAreaMode,
              tooltipParams: params.titleProps?.tooltipParams,
              ariaLabel: params.titleProps?.ariaLabel || `${translate('constants.inputAriaLabel')}`,
            }}
            placeholderProps={{
              readOnly: params.placeholderProps?.readOnly || !getComponentProps()?.globalEditEnabled,
              ariaLabel: params.placeholderProps?.ariaLabel || `${translate('constants.inputAriaLabel')}`,
            }}
            dropdownProps={{
              readOnly: params.dropdownProps?.readOnly || !getComponentProps()?.globalEditEnabled,
              ariaLabel: params.dropdownProps?.ariaLabel || `${translate('constants.inputAriaLabel')}`,
            }}
            onChangeTitle={onChange({
              updateType: params.updateValues?.title?.updateType,
              updateKey: params.updateValues?.title?.updateValue,
              valueId: params.updateValues?.title?.valueId,
            })}
            onChangeInput={onChange({
              updateType: params.updateValues?.placeholder?.updateType,
              updateKey: params.updateValues?.placeholder?.updateValue,
              valueId: params.updateValues?.placeholder?.valueId,
            })}
            onChangeDropdown={onChange({
              updateType: params.updateValues?.tooltip?.updateType,
              updateKey: params.updateValues?.tooltip?.updateValue,
              valueId: params.updateValues?.tooltip?.valueId,
            })}
          />
        );
      case 'custom-attribute-selector':
        return (
          <CustomAttributeSelector
            inputSelectorId={id}
            placeholder={translate(selectorConfig.placeholder)}
            defaultSelectedOption={getInputComponentValue(generateAttributeValueId(selectorConfig.valueId, params.attributeId))}
            options={selectorConfig.options}
            onChange={onChange({
              updateType: selectorConfig.updateType,
              updateKey: getAttributeUpdateKey(params.attributePath, selectorConfig.updateValue),
              valueId: generateAttributeValueId(selectorConfig.valueId, params.attributeId),
            })}
            editMode={getComponentProps()?.globalEditEnabled}
            selectedVariantProps={{
              title: getInputComponentValue(generateAttributeValueId(selectorConfig.attributeProps.updateValues.title.valueId, params.attributeId)) ?? '',
              placeholder: getInputComponentValue(generateAttributeValueId(selectorConfig.attributeProps.updateValues.placeholder.valueId, params.attributeId)) ?? '',
              defaultResetValueTitle: selectorConfig.attributeProps.defaultResetValueTitle,
              defaultResetValuePlaceholder: selectorConfig.attributeProps.defaultResetValuePlaceholder,
              items: getInputComponentValue(generateAttributeValueId(selectorConfig.attributeProps.updateValues.dropdown.valueId, params.attributeId)),
              titleRules: {
                onSubmit: selectorConfig.attributeProps.titleRules.onSubmitValidationRules,
                onChange: selectorConfig.attributeProps.titleRules.onChangeValidationRules,
                onChangeErrors: selectorConfig.attributeProps.titleRules.onChangeValidationErrors,
                submitErrors: selectorConfig.attributeProps.titleRules.onSubmitValidationErrors,
                maxLength: selectorConfig.attributeProps.titleRules.maxLength,
                tooLongError: translate(selectorConfig.attributeProps.titleRules.tooLongError),
              },
              placeholderRules: {
                onSubmit: selectorConfig.attributeProps.placeholderRules.onSubmitValidationRules,
                onChange: selectorConfig.attributeProps.placeholderRules.onChangeValidationRules,
                onChangeErrors: selectorConfig.attributeProps.placeholderRules.onChangeValidationErrors,
                submitErrors: selectorConfig.attributeProps.placeholderRules.onSubmitValidationErrors,
                maxLength: selectorConfig.attributeProps.placeholderRules.maxLength,
                tooLongError: translate(selectorConfig.attributeProps.placeholderRules.tooLongError),
              },
              dropdownRules: {
                maxCount: selectorConfig.attributeProps.dropdownRules.maxCount,
              },
              dropdownItemRules: {
                onSubmit: selectorConfig.attributeProps.dropdownItemRules.onSubmitValidationRules,
                onChange: selectorConfig.attributeProps.dropdownItemRules.onChangeValidationRules,
                onChangeErrors: selectorConfig.attributeProps.dropdownItemRules.onChangeValidationErrors,
                submitErrors: selectorConfig.attributeProps.dropdownItemRules.onSubmitValidationErrors,
                maxLength: selectorConfig.attributeProps.dropdownItemRules.maxLength,
                tooLongError: translate(selectorConfig.attributeProps.dropdownItemRules.tooLongError),
              },
              showAsRequired: selectorConfig.attributeProps.showAsRequired,
              titleProps: {
                readOnly: selectorConfig.attributeProps.titleProps?.readOnly || !getComponentProps()?.globalEditEnabled,
                textAreaMode: selectorConfig.attributeProps.titleProps?.textAreaMode,
                ariaLabel: selectorConfig.attributeProps.titleProps.ariaLabel || translate('constants.inputAriaLabel'),
              },
              placeholderProps: {
                readOnly: selectorConfig.attributeProps.placeholderProps?.readOnly || !getComponentProps()?.globalEditEnabled,
                ariaLabel: selectorConfig.attributeProps.placeholderProps?.ariaLabel || translate('constants.inputAriaLabel'),
              },
              dropdownProps: {
                readOnly: selectorConfig.attributeProps.dropdownProps?.readOnly || !getComponentProps()?.globalEditEnabled,
                ariaLabel: selectorConfig.attributeProps.dropdownProps?.ariaLabel || translate('constants.inputAriaLabel'),
              },
              onChangeTitle: onChange({
                updateType: selectorConfig.attributeProps.updateValues.title.updateType,
                updateKey: getAttributeUpdateKey(params.attributePath, selectorConfig.attributeProps.updateValues.title.updateValue),
                valueId: generateAttributeValueId(selectorConfig.attributeProps.updateValues.title.valueId, params.attributeId),
              }),
              onChangeInput: onChange({
                updateType: selectorConfig.attributeProps.updateValues.placeholder.updateType,
                updateKey: getAttributeUpdateKey(params.attributePath, selectorConfig.attributeProps.updateValues.placeholder.updateValue),
                valueId: generateAttributeValueId(selectorConfig.attributeProps.updateValues.placeholder.valueId, params.attributeId),
              }),
              onChangeDropdown: onChange({
                updateType: selectorConfig.attributeProps.updateValues.dropdown.updateType,
                updateKey: getAttributeUpdateKey(params.attributePath, selectorConfig.attributeProps.updateValues.dropdown.updateValue),
                valueId: generateAttributeValueId(selectorConfig.attributeProps.updateValues.dropdown.valueId, params.attributeId),
              }),
            }}
          />
        );
      case 'return-item':
        return <ReturnItem selectorId={id} product={params.product} />;
      case 'locations':
        return <Locations selectorId={id} locationsData={params.locationsData} />;
      case 'qr-code':
        return <QRRenderer selectorId={id} label={params.label} />;
      case 'editable-button':
        return (
          <InlineEditableButton
            inputSelectorId={id}
            onClick={onChangeFunc}
            inputHeight={params.inputHeight}
            inputWidth={params.inputWidth}
            fullWidth={params.fullWidth}
            fontSize={params.fontSize}
            hasMargin={params.hasMargin}
            isLozenge={params.isLozenge}
            disabled={params.disabled}
            readOnly={params.readOnly}
            centered={params.centered}
            useMerchantThemeColor={params.useMerchantThemeColor}
            backgroundColor={params.useMerchantAccentColor ? getComponentProps()?.accentColor || params.backgroundColor : params.backgroundColor}
            textColor={params.textColor}
            titleProps={{
              key: `${id}-title`,
              inputSelectorId: `${id}-title` || params.titleProps.title,
              maxLength: params.titleProps.maxLength,
              fontSize: params.titleProps.fontSize,
              fontWeight: params.titleProps.fontWeight,
              errorLeftAligned: params.titleProps.errorLeftAligned,
              hideValidationErrors: params.titleProps.hideValidationErrors,
              subtitle: params.titleProps.subtitle,
              partiallyEditable: params.titleProps.partiallyEditable,
              uneditableContent: translate(params.titleProps.uneditableContent),
              ariaLabel: params.titleProps.ariaLabel || translate('constants.inputAriaLabel'),
              validationRules: {
                onSubmit: params.titleProps.onSubmitValidationRules,
                onChange: params.titleProps.onChangeValidationRules,
                onChangeErrors: params.titleProps.onChangeValidationErrors,
                submitErrors: params.titleProps.onSubmitValidationErrors,
                tooLongError: translate(params.titleProps.tooLongError),
              },
              defaultValue: getInputComponentValue(params.updateValues?.title?.valueId) ?? '',
              defaultResetValue: params.titleProps.defaultResetValue,
              textAreaMode: params.titleProps.textAreaMode,
              checkboxMode: params.titleProps.checkboxMode,
              asLink: params.titleProps.asLink,
              centered: params.titleProps.centered,
              inputTextAlignment: params.titleProps.inputTextAlignment,
              readOnly: params.titleProps.readOnly || !getComponentProps()?.globalEditEnabled,
              onChange: onChange({
                updateType: params.updateValues?.title?.updateType,
                updateKey: params.updateValues?.title?.updateValue,
                valueId: params.updateValues?.title?.valueId,
              }),
              textColor: params.titleProps.textColor,
            }}
            tooltipProps={{
              key: `${id}-tooltip`,
              inputSelectorId: `${id}-tooltip` || params.tooltipProps.title,
              maxLength: params.tooltipProps.maxLength,
              fontSize: params.tooltipProps.fontSize,
              fontWeight: params.tooltipProps.fontWeight,
              errorLeftAligned: params.tooltipProps.errorLeftAligned,
              hideValidationErrors: params.tooltipProps.hideValidationErrors,
              subtitle: params.tooltipProps.subtitle,
              partiallyEditable: params.tooltipProps.partiallyEditable,
              uneditableContent: translate(params.tooltipProps.uneditableContent),
              ariaLabel: params.tooltipProps.ariaLabel || translate('constants.inputAriaLabel'),
              validationRules: {
                onSubmit: params.tooltipProps.onSubmitValidationRules,
                onChange: params.tooltipProps.onChangeValidationRules,
                onChangeErrors: params.tooltipProps.onChangeValidationErrors,
                submitErrors: params.tooltipProps.onSubmitValidationErrors,
                tooLongError: translate(params.tooltipProps.tooLongError),
              },
              defaultTooltip: getInputComponentValue(params.updateValues?.tooltip?.valueId) ?? '',
              defaultResetValue: params.tooltipProps.defaultResetValue,
              textAreaMode: params.tooltipProps.textAreaMode,
              checkboxMode: params.tooltipProps.checkboxMode,
              asLink: params.tooltipProps.asLink,
              centered: params.tooltipProps.centered,
              inputTextAlignment: params.tooltipProps.inputTextAlignment,
              readOnly: params.tooltipProps.readOnly || !getComponentProps()?.globalEditEnabled,
              onChange: onChange({
                updateType: params.updateValues?.tooltip?.updateType,
                updateKey: params.updateValues?.tooltip?.updateValue,
                valueId: params.updateValues?.tooltip?.valueId,
              }),
              description: translate(params.tooltipProps.description),
            }}
          />
        );
      case 'decorative-image':
        return (
          <DecorativeImage imageUrl={params.imageUrl} />
        );
      case 'editable-in-store-returns-block':
        return (
          <EditableInStoreReturnsBlock
            customTextValues={defaultValue ?? {}}
            editEnabled={getComponentProps()?.globalEditEnabled}
            onChange={onChangeFunc}
          />
        );
      case 'editable-refund-methods-block':
        return (
          <EditableRefundMethodsBlock
            customTextValues={defaultValue ?? {}}
            editEnabled={getComponentProps()?.globalEditEnabled}
            product={params.product}
            onChange={onChangeFunc}
          />
        );
      case 'editable-gift-returns-block': {
        const titleUpdateConfig = params.updateValues?.title;

        return (
          <EditableGiftReturnsBlock
            inputSelectorId={id}
            customTextValues={titleUpdateConfig ? {
              title: getInputComponentValue(titleUpdateConfig.valueId),
            } : null}
            editEnabled={Boolean(titleUpdateConfig) && getComponentProps()?.globalEditEnabled}
            titleDefaultResetValue={params.titleDefaultResetValue}
            buttonText={params.buttonText}
            onTitleChange={titleUpdateConfig ? onChange({
              updateType: titleUpdateConfig.updateType,
              updateKey: titleUpdateConfig.updateValue,
              valueId: titleUpdateConfig.valueId,
            }) : undefined}
          />
        );
      }
      case 'toggle-list':
        return (
          <ToggleList
            inputSelectorId={id}
            generateControl={generateControl}
            label={translate(params.title)}
            subControls={params.subControls}
            addChangesOnRender={params.addChangesOnRender}
          />
        );
      case 'group-required-validator':
        return (
          <GroupRequiredValidator
            key={id}
            inputSelectorId={id}
            required={params.required}
            validationRules={{
              requiredError: translate(params.requiredError),
            }}
            valueIds={params.valueIds}
          />
        );
      default:
        return null;
    }
  };

  return generate;
};

export default generateControls;
